import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ParticleField from '../components/ParticleField';
import { Reveal } from '../components/Footer';
import { brand, founder, buyerRoles, caseExamples, commitments } from '../mock';

function PageHero({ eyebrow, title, intro }) {
  return (
    <section className="hero-dark relative overflow-hidden">
      <ParticleField density={0.8} />
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 pt-40 pb-20 md:pt-44 md:pb-24">
        <Reveal className="max-w-3xl">
          <p className="eyebrow text-[#c6a94b]">{eyebrow}</p>
          <h1 className="mt-5 font-serif text-[40px] md:text-[60px] leading-[1.08] text-white">{title}</h1>
          {intro && <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[#c4d0cd]">{intro}</p>}
        </Reveal>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-[#faf9f5]" />
    </section>
  );
}

export default function About() {
  return (
    <div className="App">
      <PageHero
        eyebrow="About Herbert Project Partners"
        title="Human-led. Governance-first. Commercially grounded."
        intro="Herbert Project Partners brings structure, governance and commercial discipline to emerging technology; so organisations can adopt AI with confidence."
      />

      {/* Founder */}
      <section className="bg-[#faf9f5] py-24">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 grid gap-12 lg:grid-cols-12 lg:items-stretch">
          <Reveal className="lg:col-span-5 h-full">
            <div className="relative flex h-full min-h-[420px] flex-col items-center justify-center rounded-3xl border border-[#e7e5dd] bg-white p-10 lift">
              {founder.photo ? (
                <img
                  src={founder.photo}
                  alt={founder.name}
                  className="h-44 w-44 rounded-full object-cover ring-4 ring-[#eef2f0] shadow-[0_18px_40px_-18px_rgba(23,45,48,0.5)]"
                />
              ) : (
                <div className="flex h-44 w-44 items-center justify-center rounded-full bg-gradient-to-br from-[#2b6d7c] to-[#c6a94b] font-serif text-[52px] text-white">
                  CH
                </div>
              )}
              <div className="mt-6 text-center">
                <h2 className="font-serif text-[30px] text-[#22312f]">{founder.name}</h2>
                <p className="mt-1 text-[13px] font-semibold tracking-wide text-[#1f5866]">{founder.role}</p>
              </div>
              <div className="brand-rule mx-auto mt-5" />
              <p className="mt-6 max-w-sm text-center text-[14px] leading-relaxed text-[#5a6668]">{founder.model}</p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <p className="eyebrow text-[#c6a94b]">Founder profile</p>
            <h3 className="mt-4 font-serif text-[32px] md:text-[40px] leading-tight text-[#22312f]">
              Over a decade connecting Strategy, Evidence and Outcomes
            </h3>
            <div className="mt-6 space-y-5">
              {founder.bio.map((para) => (
                <p key={para.slice(0, 32)} className="text-[16px] leading-relaxed text-[#5a6668]">{para}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Who we work with */}
      <section className="bg-white py-24 border-y border-[#eceae2]">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="eyebrow text-[#1f5866]">Who we work with</p>
            <h3 className="mt-4 font-serif text-[34px] md:text-[44px] leading-tight text-[#22312f]">
              Built for executives and regulated organisations
            </h3>
            <p className="mt-5 text-[16px] leading-relaxed text-[#5a6668]">
              Herbert Project Partners is designed for leaders who need AI to be accountable, defensible and commercially sound - not experimental.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <ul className="space-y-3">
              {buyerRoles.map((r) => (
                <li key={r} className="flex items-center gap-3 rounded-xl border border-[#eceae2] bg-[#faf9f5] px-5 py-4">
                  <CheckCircle2 className="h-5 w-5 text-[#c6a94b] shrink-0" />
                  <span className="text-[15px] text-[#22312f]">{r}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Selected examples */}
      <section className="bg-[#faf9f5] py-24">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="max-w-2xl">
            <p className="eyebrow text-[#c6a94b]">Selected examples</p>
            <h3 className="mt-4 font-serif text-[34px] md:text-[44px] leading-tight text-[#22312f]">Anonymised project examples</h3>
            <p className="mt-5 text-[15px] leading-relaxed text-[#5a6668]">Illustrative of the outcomes we help create. Client-identifying details are withheld until permission is documented.</p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {caseExamples.map((c, i) => (
              <Reveal key={c.title} delay={i * 90}>
                <div className="lift h-full rounded-2xl border border-[#eceae2] bg-white p-7">
                  <span className="inline-block rounded-full bg-[#eef3f1] px-3 py-1 text-[11px] font-semibold tracking-wide text-[#1f5866]">{c.sector}</span>
                  <h4 className="mt-4 font-serif text-[22px] text-[#22312f]">{c.title}</h4>
                  <p className="mt-3 text-[14px] leading-relaxed text-[#5a6668]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section id="commitments" className="bg-white py-24 border-t border-[#eceae2] scroll-mt-24">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="max-w-2xl">
            <p className="eyebrow text-[#1f5866]">Trust &amp; governance</p>
            <h3 className="mt-4 font-serif text-[34px] md:text-[44px] leading-tight text-[#22312f]">Our commitments</h3>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {commitments.map((c, i) => (
              <Reveal key={c.title} delay={i * 100}>
                <div className="h-full rounded-2xl border border-[#eceae2] bg-[#faf9f5] p-8">
                  <h4 className="font-serif text-[24px] text-[#22312f]">{c.title}</h4>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-[#5a6668]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}

export function CtaBand() {
  return (
    <section className="bg-[#faf9f5] pb-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="hero-dark relative overflow-hidden rounded-3xl px-8 py-14 md:px-14 md:py-16">
          <ParticleField density={0.5} />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <h2 className="font-serif text-[30px] md:text-[40px] leading-tight text-white max-w-xl">
              Tomorrow with AI begins here
            </h2>
            <Link to="/contact">
              <Button className="btn-gold rounded-full h-12 px-7 text-[14px] font-semibold whitespace-nowrap">
                {brand.primaryCta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
